import { WorldStanceSwitcher } from "./WorldStanceSwitcher";
import { DESK_DOCK_TIGHT_PX, useDockWide } from "./dockPreferences";
import { releaseDockYield, useDeskWidthStore } from "./cleoWide";
import { ChevronsLeftIcon, ChevronsRightIcon } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";

import { DockScrollContents } from "./DockScrollContents";
import { cn } from "~/lib/utils";
import { useWorldViewIfSeated } from "~/neucleos/worldView";
import { RoomRailSection } from "~/components/RoomRailSection";

/** The left dock holds the room cards (16 Oct 2026); account doors live at
 * the foot of the right context dock, below Music. */
const DOCK_WIDTH_KEY = "cleo:desk-dock:width";
const WIDTH_MIN = 160;
const WIDTH_MAX = 320;

function readLatch(key: string, min: number, max: number): number | null {
  try {
    const raw = Number(window.localStorage.getItem(key));
    if (Number.isFinite(raw) && raw >= min && raw <= max) return Math.round(raw);
  } catch {
    // Per-viewer convenience only.
  }
  return null;
}

function writeLatch(key: string, value: number | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch {
    // Storage refusal never breaks the dock.
  }
}

export function DeskDock() {
  const worldView = useWorldViewIfSeated();
  const activeState = worldView?.state;
  // Widen Cleo (dated 2026-10-07): when Cleo's plan needs the dock's room the
  // dock renders tight and its stored preference stays as the person left it,
  // so turning Widen off brings it back. Expanding it then is the newer hand
  // act: it turns Widen off instead of doing nothing. A held Context open
  // (the CONTEXT rib, dated 2026-10-07) yields the dock the same way:
  // expanding then ends the open, and turns Widen off only when Widen alone
  // would still keep the dock tight (releaseDockYield).
  const [wideWanted, setWideWanted] = useDockWide();
  const yieldedToCleo = useDeskWidthStore((state) => state.plan?.dockTight ?? false);
  // While Widen Cleo holds, a width drag stops where her plan still stands,
  // so releasing it never folds the dock the hand just widened.
  const planDockMax = useDeskWidthStore((state) => state.plan?.dockMaxPx ?? null);
  const widthMax =
    planDockMax === null ? WIDTH_MAX : Math.max(WIDTH_MIN, Math.min(WIDTH_MAX, planDockMax));
  const wide = wideWanted && !yieldedToCleo;
  const setWide = (next: boolean) => {
    if (next && yieldedToCleo) releaseDockYield();
    else setWideWanted(next);
  };
  const [dockWidth, setDockWidth] = useState<number | null>(() =>
    typeof window === "undefined" ? null : readLatch(DOCK_WIDTH_KEY, WIDTH_MIN, WIDTH_MAX),
  );
  // The plan's dock term: the width the dock renders when nothing needs its
  // room. A width drag publishes on release, never per tick, so the plan
  // cannot fold the dock out from under the hand that is widening it.
  const [widthDragging, setWidthDragging] = useState(false);
  useLayoutEffect(() => {
    if (widthDragging && wide) return;
    const { setDockPreferredPx } = useDeskWidthStore.getState();
    setDockPreferredPx(wideWanted ? (dockWidth ?? WIDTH_MIN) : DESK_DOCK_TIGHT_PX);
  }, [dockWidth, wide, wideWanted, widthDragging]);
  useLayoutEffect(() => () => useDeskWidthStore.getState().setDockPreferredPx(null), []);
  const endWidthDrag = () => {
    if (widthDrag.current === null) return;
    widthDrag.current = null;
    setWidthDragging(false);
    setDockWidth((value) => {
      writeLatch(DOCK_WIDTH_KEY, value);
      return value;
    });
  };
  const widthDrag = useRef<{ x: number; width: number } | null>(null);

  return (
    <div
      className={cn(
        // FD-CAN-29: the context dock's mirror — 48px with symmetric padding
        // 10 (chip + 2·10, the design's own proportion), so the session chip
        // stands at frame line − curl − gap (10.5) from the left card's edge
        // and the column is the dock's center; no border of the dock's own
        // (the card's edge is the frame line, and the room frame draws none).
        // DATED 2026-09-17 (the principal's crop of the dock's foot: the last
        // chip's corner ran into the card's curve): the last door stands on
        // the card's corner TANGENT — PANEL_CARD_RADIUS (16) above the card's
        // 14 foot, 30 from the shell. The pool below the launchers carries the
        // dock's 10 gap, so the foot itself is 20. The context dock and the
        // account fallback wear the same 30 with no pool gap.
        "relative flex h-full min-h-0 flex-none flex-col items-stretch gap-2.5 pt-2 pb-[20px]",
        wide ? "px-2.5" : "w-12 items-center px-2.5",
      )}
      data-desk-dock=""
      data-dock-variant={wide ? "wide" : "tight"}
      style={wide ? { width: `${dockWidth ?? WIDTH_MIN}px` } : undefined}
    >
      <div className="flex h-[26px] w-full shrink-0 items-center gap-2" data-dock-stance-row="">
        {wide ? (
          <div className="min-w-0 flex-1">
            <WorldStanceSwitcher />
          </div>
        ) : null}
        <button
          aria-label={wide ? "Compact dock" : "Expand dock"}
          className={cn(
            // FD-CAN-33, DATED REFINEMENT 2026-09-02 (the principal's crop of
            // both docks' toggles: "we should not have the circle for those …
            // that should be consistent, right?"): the toggle keeps the
            // design's 26px circle and SHEDS the 16% inset ring. The ring now
            // means one thing — the create/launch mark (the "+" wears
            // [data-deck-plus]) — so ambient chrome that never moves shows its
            // container on hover alone. The right dock's toggle is the same
            // control mirrored and wears exactly this.
            "flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground",
            // FD-CAN-29: the tight column centers the toggle by itself; the wide
            // dock keeps it at the card's side.
            wide ? "ml-auto" : "mx-auto",
          )}
          data-dock-toggle=""
          onClick={() => setWide(!wide)}
          type="button"
        >
          {wide ? (
            <ChevronsLeftIcon className="size-3.5" />
          ) : (
            <ChevronsRightIcon className="size-3.5" />
          )}
        </button>
      </div>
      {/* The rail-law chip moved to the room's breadcrumb spine (FD-CAN-04
          dated refinement 2026-09-01): a room instrument rides the room's
          own row, and a horizontal control fights this narrow column. */}
      {/* "+New Child" needs the wide dock for its name, a hand act like
          the toggle; revealing a room is not, so while the dock has
          yielded to Widen Cleo it stays tight and Widen stays on. */}
      <RoomRailSection
        wide={wide}
        onExpand={() => setWide(true)}
        onReveal={() => {
          if (!yieldedToCleo) setWideWanted(true);
        }}
        // The principal, 16 Oct 2026 ("remove the divider … general sessions
        // are just another room"): one region of room cards, General among
        // the Workrooms, scrolling as one; no divider, no door chips. And
        // 18 Oct 2026 ("remove the header items from the scroll area"): the
        // rail's header stands above this region; only the rooms scroll.
        scrollFrame={(rows) => (
          <div
            className="relative -ml-3 flex min-h-0 w-[calc(100%+12px)] min-w-0 flex-1 flex-col"
            data-dock-room-rail-region=""
          >
            {/* RM-CAN-14: the same region lists Local's folders; its scroll buttons say so. */}
            <DockScrollContents label={activeState?.kind === "local" ? "folders" : "rooms"}>
              {rows}
            </DockScrollContents>
          </div>
        )}
      />
      {/* The right border drags to resize the dock's width (the crop's
          horizontal arrow), wide mode only. */}
      {wide ? (
        <div
          aria-label="Resize dock width — drag"
          aria-orientation="vertical"
          className="absolute -right-1 top-0 h-full w-2 cursor-col-resize focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          data-dock-width-handle=""
          onKeyDown={(event) => {
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
            event.preventDefault();
            const current = dockWidth ?? WIDTH_MIN;
            const next = Math.min(
              widthMax,
              Math.max(WIDTH_MIN, current + (event.key === "ArrowRight" ? 16 : -16)),
            );
            setDockWidth(next);
            writeLatch(DOCK_WIDTH_KEY, next);
          }}
          onPointerDown={(event) => {
            widthDrag.current = { x: event.clientX, width: dockWidth ?? WIDTH_MIN };
            setWidthDragging(true);
            (event.target as HTMLElement).setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (widthDrag.current === null) return;
            // A release this handle never heard ends the drag on the next move.
            if (event.buttons === 0) {
              endWidthDrag();
              return;
            }
            const next = Math.min(
              widthMax,
              Math.max(WIDTH_MIN, widthDrag.current.width + (event.clientX - widthDrag.current.x)),
            );
            setDockWidth(next);
          }}
          onPointerUp={endWidthDrag}
          onPointerCancel={endWidthDrag}
          onLostPointerCapture={endWidthDrag}
          role="separator"
          tabIndex={0}
        />
      ) : null}
    </div>
  );
}
